import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TreeNavigator from "@/components/TreeNavigator";
import { Database, ExternalLink, Search, ArrowRight, Download } from "lucide-react";

export default function DatasetsPublic() {
  const [nodes, setNodes] = useState([]);
  const [nodesLoaded, setNodesLoaded] = useState(false);
  const [selected, setSelected] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/taxonomy", { params: { domain: "datasets" } })
      .then((r) => setNodes(r.data || []))
      .catch(() => setNodes([]))
      .finally(() => setNodesLoaded(true));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (selected) params.taxonomy_id = selected.id;
    if (search) params.q = search;
    api.get("/datasets", { params })
      .then((r) => { if (!cancelled) setDatasets(r.data || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selected, search]);

  return (
    <div className="min-h-screen bg-kb-bg" data-testid="datasets-public-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PUBLIC DATA CATALOGUE</div>
        <h1 className="font-serif text-4xl lg:text-5xl text-kb-ink mt-2 leading-tight">
          <em className="italic text-kb-primary">Curated datasets</em> for every domain.
        </h1>
        <p className="mt-3 text-kb-ink-muted max-w-2xl">
          Hand-organised list of publicly available datasets across research domains. Browse by domain hierarchy
          or search by name / keyword.
        </p>

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-4">
            <div className="kb-card p-5 sticky top-20">
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">DOMAIN HIERARCHY</div>
              <div className="mt-3 max-h-[60vh] overflow-y-auto pr-2">
                {nodes.length === 0 ? (
                  <div className="kb-mono text-xs text-kb-ink-muted py-4">Loading…</div>
                ) : (
                  <TreeNavigator nodes={nodes} selectedId={selected?.id} onSelect={setSelected} testIdPrefix="ds-tree" />
                )}
              </div>
            </div>
          </aside>
          <div className="lg:col-span-8">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-kb-ink-muted" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search datasets by name, source, or tag…"
                className="kb-input text-sm pl-9 w-full"
                data-testid="datasets-search-input"
              />
            </div>

            {selected && (
              <div className="mt-4 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                FILTERED BY <span className="text-kb-primary">{(selected.path || []).join(" › ")}</span>
              </div>
            )}

            {loading ? (
              <div className="mt-10 kb-mono text-xs text-kb-ink-muted">Loading…</div>
            ) : datasets.length === 0 ? (
              <div className="mt-12 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
                No datasets match — try a different domain or clear the search.
              </div>
            ) : (
              <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="datasets-grid">
                {datasets.map((d) => (
                  <article key={d.id} className="kb-card p-5 flex flex-col" data-testid={`dataset-${d.id}`}>
                    <div className="flex items-center justify-between">
                      <Database className="w-5 h-5 text-kb-primary" strokeWidth={1.5} />
                      {d.license && (
                        <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">{d.license}</span>
                      )}
                    </div>
                    <h3 className="font-serif text-lg text-kb-ink mt-2 leading-snug">{d.name}</h3>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                      {(d.path || []).join(" › ")}
                    </div>
                    {d.description && <p className="text-sm text-kb-ink-muted mt-3 line-clamp-3 flex-1">{d.description}</p>}
                    <div className="mt-4 pt-3 border-t border-slate-200 text-xs text-kb-ink-muted flex flex-wrap gap-2 items-center">
                      {d.source_name && <span>{d.source_name}</span>}
                      {d.size && <span>· {d.size}</span>}
                      {(d.formats || []).slice(0, 3).map((f) => (
                        <span key={f} className="kb-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-slate-200">{f}</span>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      {d.source_url && (
                        <a href={d.source_url} target="_blank" rel="noreferrer" className="kb-btn-outline text-xs flex-1 justify-center" data-testid={`dataset-${d.id}-source`}>
                          <ExternalLink className="w-3.5 h-3.5" /> Source
                        </a>
                      )}
                      {d.download_url && (
                        <a href={d.download_url} target="_blank" rel="noreferrer" className="kb-btn-primary text-xs flex-1 justify-center" data-testid={`dataset-${d.id}-download`}>
                          <Download className="w-3.5 h-3.5" /> Download
                        </a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="mt-12 kb-card p-6 text-center">
              <h3 className="font-serif text-xl text-kb-ink">Need a dataset we haven't listed?</h3>
              <p className="text-sm text-kb-ink-muted mt-2">Tell us what you're looking for — we'll add curated entries weekly.</p>
              <Link to="/about#contact" className="kb-btn-outline text-sm mt-3 inline-flex" data-testid="datasets-request-cta">
                Request a dataset <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
